import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ITaskStockClaim } from '@interfaces/IScheduler';
import type { ITakeoffMaterial } from '@interfaces/ICadTakeoff';
import { round1, type IClaimEvaluation } from '../../stock/claimEvaluation';
import type { IOtherClaim } from '../../stock/useTaskStock';
import {
  canMeasure,
  claimQuantity,
  defaultUnitKey,
  unitKeyOfLabel,
  unitKeysFor,
} from '../../stock/claimElements';
import { FREE_TEXT_OPTION, materialOptions } from '../../stock/claimRowOptions';
import { sameUnit } from '@d-orca/shared/client/logistics';
import { unitOptions } from '../../../LogisticsPage/units';
import { PickCombo, type IPickComboOption } from '@components/ui/PickCombo/PickCombo';
import { Tooltip, TooltipContent } from '@components/ui/Tooltip/Tooltip';
import { StockClaimElements } from './StockClaimElements';
import styles from './TaskPanelStockTab.module.css';

interface IStockClaimRowProps {
  claim: ITaskStockClaim;
  evaluation: IClaimEvaluation;
  /** The floor's take-off rows, ungrouped — the Material pick list only. */
  materials: ITakeoffMaterial[];
  /** The grouped rows for a material (`useTaskStock.rowsFor`). A resolver
   *  rather than a value because the row also needs them for the material the
   *  user is about to pick, before the parent re-renders with it. */
  rowsFor: (materialName: string) => ITakeoffMaterial[];
  /** The elements other tasks hold of THIS claim's material, on any floor,
   *  by globalId → task name. */
  taken: ReadonlyMap<string, string>;
  /** The task's floors in order, their names, and which floor an element
   *  sits on — the types block's per-floor split (`StockClaimElements`). */
  floorIds: string[];
  floorNames: string[];
  floorOf: (globalId: string) => string | null;
  cadVersionId: string | null;
  otherClaims: IOtherClaim[];
  /** The unit the site's stock for a material is tracked in, or null when
   *  Logistics has no level for it (`useTaskStock().stockUnitFor`). Passed as
   *  a resolver rather than a value because the row also needs it for the
   *  name the user is about to pick or type. */
  stockUnitFor: (materialName: string) => string | null;
  onChange: (patch: Partial<ITaskStockClaim>) => void;
  /** Opens the element list for one of the claim's types. */
  onSelect: (kind: string) => void;
}

/**
 * One claim's row: Material and Unit side by side, then the types block (one
 * line per CAD element type with its chosen elements), then the computed
 * Quantity.
 *
 * Material is the general pick — each name once per floor, its element types a
 * hint under it — because the site holds at most one stock per material. That
 * stock, when it exists, owns the unit: the field is locked to it and only the
 * stock's own Edit tab can change it. Otherwise the unit list offers exactly
 * what the CAD can measure this material in, plus pieces.
 *
 * Quantity is never typed for a CAD claim: it is the sum of the chosen
 * elements' own measures in the chosen unit, rewritten on every selection and
 * every unit change so the stored claim is always self-consistent. Only a
 * "Not in the CAD" free-text claim keeps an editable number.
 *
 * Free of query/store wiring — every number it shows comes from the
 * `evaluation` its parent (`TaskPanelStockTab`) computed via
 * `useTaskStock().evaluate(claim)`.
 */
export function StockClaimRow({
  claim,
  evaluation,
  materials,
  rowsFor,
  taken,
  floorIds,
  floorNames,
  floorOf,
  cadVersionId,
  otherClaims,
  stockUnitFor,
  onChange,
  onSelect,
}: IStockClaimRowProps) {
  const { t } = useTranslation();
  const fieldId = useId();
  // Focus the free-text box once, right after "Not in the CAD…" swaps it in —
  // not on every mount, or opening the tab on a stored free-text claim would
  // steal focus from the drawer. A ref, not state: the intent is set in the
  // click handler and consumed when the box has mounted, with nothing to
  // re-render over.
  const freeTextRef = useRef<HTMLInputElement | null>(null);
  const focusFreeTextOnMount = useRef(false);
  useEffect(() => {
    if (!focusFreeTextOnMount.current || claim.fromCad) return;
    focusFreeTextOnMount.current = false;
    freeTextRef.current?.focus();
  }, [claim.fromCad]);

  const unitLabelOf = (key: string) => t(`logistics.stockDialog.units.${key}`);
  const rows = claim.fromCad ? rowsFor(claim.materialName) : [];
  const elements = claim.elements ?? [];
  const unitKey = unitKeyOfLabel(claim.unit);
  // The unit the site tracks this material in, if it tracks it at all.
  const stockUnit = claim.materialName === '' ? null : stockUnitFor(claim.materialName);
  // Locked only while the claim actually holds the stock's unit — which every
  // pick sets — in either language, or as the stock's stored key. A claim in
  // some other unit (an older row, free text) stays editable so the mismatch its outcome reports can be
  // fixed; picking that unit locks the field again.
  const unitLocked = stockUnit !== null && sameUnit(claim.unit, stockUnit);
  // The units the CAD can actually measure THIS material in. A claim already
  // stored in something else (kg, bags — picked through the escape hatch, or
  // inherited from the site's stock) starts with the full list open, so the
  // field always offers the value it is showing.
  const cadUnitLabels = claim.fromCad ? unitKeysFor(rows).map(unitLabelOf) : [];
  const unitIsCad = cadUnitLabels.some((label) => sameUnit(label, claim.unit));
  const [unitsExpanded, setUnitsExpanded] = useState(false);
  const showAllUnits = !claim.fromCad || unitsExpanded || (claim.unit !== '' && !unitIsCad);
  // The CAD's own units always lead, in the same order, and expanding only
  // APPENDS the rest below the divider — the list grows in place rather than
  // reshuffling under the pointer.
  const cadChoices: IPickComboOption[] = cadUnitLabels.map((label) => ({ value: label, label }));
  const otherChoices: IPickComboOption[] = unitOptions(t)
    .filter((label) => !cadUnitLabels.some((cad) => sameUnit(cad, label)))
    .map((label, i) => ({ value: label, label, separatorBefore: i === 0 && cadChoices.length > 0 }));
  const unitChoices: IPickComboOption[] = showAllUnits
    ? [...cadChoices, ...otherChoices]
    : [
        ...cadChoices,
        // The same escape hatch the Material field carries: the CAD's own
        // units are the useful ones, but a site that orders this material by
        // weight or by the bag must still be able to say so.
        { value: FREE_TEXT_OPTION, label: t('scheduler.panel.stock.notInCad'), separatorBefore: true, keepsOpen: true },
      ];

  /** Can the chosen elements still produce a quantity in `key`? False once
   *  the unit is one none of the material's types is measured in — the
   *  elements then buy an unknown amount, so the user types the quantity
   *  instead. */
  function derivableIn(key: ReturnType<typeof unitKeyOfLabel>): boolean {
    return claim.fromCad && rows.some((row) => canMeasure(row, key));
  }
  const derivable = derivableIn(unitKey);
  // The user types the quantity only for a free-text claim, or for a CAD
  // claim deliberately put in a unit none of its types is measured in. A CAD
  // claim with nothing picked yet is locked at 0: there is nothing to type
  // against until a material and unit exist.
  const quantityTyped = !claim.fromCad || (claim.materialName !== '' && claim.unit !== '' && !derivable);

  /** Adopt the site stock's unit for the material just chosen or typed: stock
   *  is at most one row per material and it owns the unit. With no stock the
   *  user's own pick stands. */
  function unitPatch(materialName: string): Partial<ITaskStockClaim> {
    const unit = stockUnitFor(materialName);
    return unit === null ? {} : { unit };
  }

  function handleMaterialSelect(value: string) {
    if (value === FREE_TEXT_OPTION) {
      // The CAD name is exactly what the user said this is NOT — start the
      // free-text box empty and put the cursor in it.
      // No CAD line backs it, so no floor scopes it either (contract v7).
      onChange({ fromCad: false, materialName: '', cadVersionId: null, elements: [], floorIds: [] });
      focusFreeTextOnMount.current = true;
      return;
    }
    // A new material means new elements, so the old selection means nothing;
    // the unit re-derives from the stock (which owns it) or from whichever
    // measure most of the new material's elements can report.
    onChange({
      materialName: value,
      fromCad: true,
      cadVersionId,
      unit: stockUnitFor(value) ?? unitLabelOf(defaultUnitKey(rowsFor(value))),
      elements: [],
      quantity: 0,
    });
  }

  /** The unit changes what an element is worth, not which ones the task
   *  covers — the selection stands and the quantity is re-derived. A
   *  free-text claim has no elements to derive from, so its typed quantity is
   *  left alone. */
  function handleUnitPick(unit: string) {
    if (unit === FREE_TEXT_OPTION) {
      setUnitsExpanded(true);
      return;
    }
    setUnitsExpanded(false);
    const key = unitKeyOfLabel(unit);
    // A unit the CAD cannot measure leaves the typed quantity alone rather
    // than recomputing it to zero out of a selection that no longer means
    // anything.
    onChange(derivableIn(key) ? { unit, quantity: claimQuantity(rows, elements, key) } : { unit });
  }

  /* The tab's own sentence for the figure, and the pill's own "title: names"
     shape for its accessible name — both strings already exist. */
  const othersText = t('scheduler.panel.stock.claimedOthers', { qty: evaluation.claimedByOthers });
  const othersLabel = t('logistics.source.pillLabel', {
    title: othersText,
    tasks: otherClaims.map((o) => o.taskName).join(', '),
  });

  return (
    <div>
      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor={`${fieldId}-material`}>{t('scheduler.panel.stock.material')}</label>
          {claim.fromCad ? (
            <PickCombo
              id={`${fieldId}-material`}
              value={claim.materialName}
              options={materialOptions(materials, t)}
              placeholder={t('scheduler.panel.stock.pickMaterial')}
              onPick={handleMaterialSelect}
              // A floor can hold hundreds of materials; typing narrows them.
              filterable
            />
          ) : (
            <>
              <input
                ref={freeTextRef}
                id={`${fieldId}-material`}
                type="text"
                value={claim.materialName}
                onChange={(e) => onChange({ materialName: e.target.value, ...unitPatch(e.target.value) })}
              />
              <button
                type="button"
                className={styles.backToCad}
                onClick={() =>
                  // Going free-text cleared `cadVersionId`; flipping `fromCad`
                  // back on its own would leave a claim stamped to no version,
                  // which reads as a CAD change and raises a spurious banner.
                  // Re-stamp the current version and clear the typed-in
                  // material/unit/quantity — none survives the switch to the
                  // CAD list, where the material pick supplies the unit and the
                  // chosen elements supply the quantity.
                  onChange({
                    fromCad: true,
                    materialName: '',
                    unit: '',
                    quantity: 0,
                    elements: [],
                    floorIds,
                    cadVersionId,
                    keptOverCad: undefined,
                  })
                }
              >
                {t('scheduler.panel.stock.backToCad')}
              </button>
            </>
          )}
        </div>

        <div className={styles.field}>
          <label id={`${fieldId}-unit-label`} htmlFor={`${fieldId}-unit`}>
            {t('scheduler.panel.stock.unit')}
          </label>
          {unitLocked ? (
            /* The site's stock owns the unit — shown read-only. A <span> is
               not a labelable element, so `htmlFor` above cannot reach it;
               `aria-labelledby` points back at the label so the field keeps
               its accessible name while locked. */
            <span
              id={`${fieldId}-unit`}
              aria-labelledby={`${fieldId}-unit-label`}
              className={styles.lockedValue}
              title={t('scheduler.panel.stock.unitFromStock')}
            >
              {claim.unit}
            </span>
          ) : (
            <>
              <PickCombo
                id={`${fieldId}-unit`}
                value={claim.unit}
                options={unitChoices}
                placeholder={t('scheduler.panel.stock.pickUnit')}
                onPick={handleUnitPick}
              />
              {claim.fromCad && showAllUnits && cadUnitLabels.length > 0 && (
                <button
                  type="button"
                  className={styles.backToCad}
                  onClick={() => {
                    // Back to the unit the material would have opened on —
                    // whichever measure most of its elements report — not
                    // merely the first the list happens to show.
                    setUnitsExpanded(false);
                    handleUnitPick(unitLabelOf(defaultUnitKey(rows)));
                  }}
                >
                  {t('scheduler.panel.stock.backToCadUnits')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {claim.fromCad && rows.length > 0 && (
        <StockClaimElements
          rows={rows}
          elements={elements}
          taken={taken}
          unitKey={unitKey}
          unitLabel={claim.unit}
          total={claim.quantity}
          floorIds={floorIds}
          floorNames={floorNames}
          floorOf={floorOf}
          onSelect={onSelect}
        />
      )}

      {/* One line, not four blocks (Niv, 2026-09-16, option A): what this task
          books, what other tasks hold of the same material — the names behind
          the figure, so hover, tap and keyboard all reach them — and what the
          CAD still leaves free. A CAD amount is a SUM of the elements above,
          so it has no input; only a free-text claim keeps one. */}
      <div className={`${styles.field} ${styles.quantityField}`}>
        <label id={`${fieldId}-quantity-label`} htmlFor={`${fieldId}-quantity`}>
          {t('scheduler.panel.stock.quantity')}
        </label>
        {!quantityTyped ? (
          <p className={styles.figures}>
            <span id={`${fieldId}-quantity`} aria-labelledby={`${fieldId}-quantity-label`} className={styles.amount}>
              {claim.quantity} {claim.unit}
            </span>
            <span className={styles.context}>
            {otherClaims.length > 0 && (
              <Tooltip
                content={
                  <TooltipContent
                    title={othersText}
                    lines={otherClaims.map((o) => `${t('scheduler.panel.stock.claimedBy', { task: o.taskName })} · ${o.quantity} ${claim.unit}`)}
                  />
                }
              >
                <button type="button" className={styles.others} aria-label={othersLabel}>
                  {othersText}
                </button>
              </Tooltip>
            )}
            {evaluation.ceiling !== null && (
              <span>
                {/* The ceiling still counts this task's own pick; what is left
                    is what stays free once it is booked. A claim kept above
                    the CAD has nothing left (its own line says by how much). */}
                {t('scheduler.panel.stock.leftToClaim')}: {Math.max(0, round1(evaluation.ceiling - claim.quantity))} {claim.unit}
              </span>
            )}
            {claim.keptOverCad && evaluation.ceiling !== null && claim.quantity > evaluation.ceiling && (
              <span>
                {t('scheduler.panel.stock.keptOver', {
                  over: Math.round((claim.quantity - evaluation.ceiling) * 10) / 10,
                  unit: claim.unit,
                })}
              </span>
            )}
            </span>
          </p>
        ) : (
          <input
            id={`${fieldId}-quantity`}
            type="number"
            value={claim.quantity}
            onChange={(e) => onChange({ quantity: Number(e.target.value) })}
          />
        )}
      </div>

      {!claim.fromCad && <p className={styles.hint}>{t('scheduler.panel.stock.freeText')}</p>}

    </div>
  );
}
